import type { Tracker, TrackingEvent } from '@archipro-website/tracker';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '~/modules/user';
import type { AuthType } from '~/modules/user/type';
import { AUTH_PROVIDERS, AUTH_SOURCES, AUTH_TYPES } from '~/modules/user/type';
import { trackAuthEvent } from '~/modules/user/util/track-auth-event';
import { useDeviceBrowserContext } from '@archipro-design/aria';

export const AUTH_EVENTS: Record<
    AuthType,
    Extract<TrackingEvent, 'new_account_registered' | 'user_login'>
> = {
    'sign-up': 'new_account_registered',
    'sign-in': 'user_login',
};

export const AUTH_TYPE_PARAM_KEY = 'auth-type';
export const AUTH_SOURCE_PARAM_KEY = 'auth-source';
export const AUTH_PROVIDER_PARAM_KEY = 'provider';

export const useAuthEvent = (tracker?: Tracker) => {
    const user = useUser();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isFirefox } = useDeviceBrowserContext();

    const isMember = user?.__typename === 'Me';
    const guestID = user?.__typename === 'Me' ? user.TrackedGuest?.ID : 0;

    useEffect(() => {
        /**
         * get the provider, authSource and authType from the searchParams
         * if user sign up or sign in, we will add this before redirecting
         * social auth check: app/src/Component/Page/SocialAuthController.php
         * email & password auth check: LoginSignUpForm
         */
        const providerParams = searchParams.get(AUTH_PROVIDER_PARAM_KEY);
        const provider = AUTH_PROVIDERS.find((p) => p === providerParams);

        const authTypeParam = searchParams.get(AUTH_TYPE_PARAM_KEY);
        const authType = AUTH_TYPES.find((t) => t === authTypeParam);
        const event = authType ? AUTH_EVENTS[authType] : undefined;

        const authSourceParam = searchParams.get(AUTH_SOURCE_PARAM_KEY);
        const authSource =
            AUTH_SOURCES.find((source) => source === authSourceParam) ??
            AUTH_SOURCES[0];

        if (!isMember || !provider || !tracker || !event) return;
        const logEvent = async () => {
            await trackAuthEvent({
                tracker,
                event,
                provider,
                authSource,
                guestID,
            });

            // Create a new URLSearchParams object and remove the auth parameters
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete(AUTH_PROVIDER_PARAM_KEY);
            newSearchParams.delete(AUTH_TYPE_PARAM_KEY);
            newSearchParams.delete(AUTH_SOURCE_PARAM_KEY);

            // Update the URL search parameters
            setSearchParams(newSearchParams);
        };
        logEvent();
    }, [isMember, tracker, searchParams, guestID, isFirefox, setSearchParams]);
};
